import { useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { track, EVENTS } from "@/lib/analytics";
import { Mail, User, ArrowRight, CheckCircle2 } from "lucide-react";

/**
 * Compact lead-capture modal used on the Login page.
 * Captures Email + Name BEFORE the OAuth flow, persists to `user_access_logs`
 * (visible in Analytics → Auditoria de Acessos) and fires a GA event.
 */
export default function LeadCaptureModal({ open, onClose, onContinue }) {
  const { t, locale } = useI18n();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!open) return null;

  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && name.trim().length >= 2;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      await api.post("/access/log", { email, name, source: "lead_capture" });
      track(EVENTS.LEAD_FORM_SUBMIT, { source: "lead_capture" });
      track(EVENTS.COCKPIT_LOGIN, { source: "lead_capture" });
      setDone(true);
      setTimeout(() => onContinue?.({ email, name }), 800);
    } catch {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6" data-testid="lead-capture-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md border border-[#1E3A8A] bg-[#0F172A] rounded-sm shadow-2xl">
        <div className="border-b border-slate-800 px-6 py-4">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-slate-500 mb-2">
            {locale === "pt" ? "Acesso ao Cockpit · MVP" : "Cockpit Access · MVP"}
          </div>
          <h3 className="font-display font-black text-2xl tracking-tight">
            {locale === "pt" ? "Registre seu acesso" : "Register your access"}
          </h3>
          <p className="text-xs text-slate-400 mt-1">
            {locale === "pt"
              ? "Antes de entrar no protótipo, informe seu e-mail corporativo. Suas credenciais serão registradas para auditoria."
              : "Before entering the prototype, please provide your corporate email. Your credentials will be logged for audit."}
          </p>
        </div>

        {done ? (
          <div className="p-8 flex flex-col items-center text-center" data-testid="lead-capture-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mb-3" />
            <div className="font-display text-xl font-bold mb-1">
              {locale === "pt" ? "Acesso registrado" : "Access logged"}
            </div>
            <div className="text-xs text-slate-400">
              {locale === "pt" ? "Redirecionando ao login Google..." : "Redirecting to Google login..."}
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500 block mb-2">
                {locale === "pt" ? "Nome completo" : "Full name"}
              </label>
              <div className="flex items-center border border-slate-800 bg-[#020617] focus-within:border-[#3B82F6]">
                <User className="w-4 h-4 text-slate-500 ml-3" />
                <input
                  data-testid="lead-name-input"
                  type="text"
                  required
                  minLength={2}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={locale === "pt" ? "Ex: Ana Silva" : "Ex: John Doe"}
                  className="flex-1 bg-transparent px-3 py-2.5 text-sm text-slate-100 outline-none"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500 block mb-2">
                {locale === "pt" ? "E-mail corporativo" : "Corporate email"}
              </label>
              <div className="flex items-center border border-slate-800 bg-[#020617] focus-within:border-[#3B82F6]">
                <Mail className="w-4 h-4 text-slate-500 ml-3" />
                <input
                  data-testid="lead-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nome@orgao.pe.gov.br"
                  className="flex-1 bg-transparent px-3 py-2.5 text-sm text-slate-100 outline-none"
                />
              </div>
            </div>

            <button
              data-testid="lead-submit-btn"
              type="submit"
              disabled={!valid || submitting}
              className="w-full bg-[#1E3A8A] hover:bg-[#1D4ED8] disabled:opacity-50 text-white font-semibold py-3 rounded-sm flex items-center justify-center gap-2 transition-colors"
            >
              {submitting
                ? (locale === "pt" ? "Registrando..." : "Logging...")
                : (locale === "pt" ? "Continuar para o login" : "Continue to login")}
              <ArrowRight className="w-4 h-4" />
            </button>

            <button type="button" onClick={onClose} className="w-full text-[10px] font-mono uppercase tracking-wider text-slate-500 hover:text-slate-300 transition-colors">
              {locale === "pt" ? "Cancelar" : "Cancel"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
